import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { InfoLayout } from "@/components/laneguide/InfoLayout";
import { LoanWarning } from "@/components/laneguide/LoanWarning";
import { LenderTable } from "@/components/laneguide/LenderTable";
import { FaqSection } from "@/components/laneguide/PageFaq";
import { LENDER_LIST, APPLY_URLS, type Lender } from "@/lib/lenders";
import { EpiProvider } from "@/lib/epi";

export const Route = createFileRoute("/guider/lanekalkyl")({
  head: () => ({
    meta: [
      { title: "Lånekalkyl – räkna ut månadskostnad och ränta | Svensk Låneguide" },
      { name: "description", content: "Räkna ut månadskostnad, total kostnad och effektiv ränta på ditt lån. Jämför långivare och läs hur du får lån även om det är svårt." },
      { property: "og:title", content: "Lånekalkyl – räkna ut månadskostnad och ränta" },
      { property: "og:description", content: "Räkna ut månadskostnad, total kostnad och effektiv ränta på lån mellan 10 000 och 800 000 kr." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "index, follow" },
    ],
  }),
  validateSearch: (search: Record<string, unknown>): {
    amount?: number; years?: number; months?: number; rate?: number; setup?: number; avi?: number; type?: "annuitet" | "rak";
  } => ({
    amount: search.amount != null ? Number(search.amount) || undefined : undefined,
    years: search.years != null ? Number(search.years) || undefined : undefined,
    months: search.months != null ? Number(search.months) || undefined : undefined,
    rate: search.rate != null ? Number(search.rate) || undefined : undefined,
    setup: search.setup != null ? Number(search.setup) || undefined : undefined,
    avi: search.avi != null ? Number(search.avi) || undefined : undefined,
    type: search.type === "rak" ? ("rak" as const) : search.type === "annuitet" ? ("annuitet" as const) : undefined,
  }),
  component: Kalkyl,
});

/* ---------- helpers ---------- */

const nf = new Intl.NumberFormat("sv-SE");
const formatKr = (n: number) => nf.format(Math.round(n)) + " kr";
const formatPct = (n: number) => n.toFixed(2).replace(".", ",") + " %";

function annuityPayment(amount: number, months: number, monthlyRate: number) {
  if (monthlyRate <= 0) return amount / months;
  return (amount * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -months));
}

/** Returns payment schedule (excluding avi fee) */
function schedule(amount: number, months: number, annualRate: number, type: "annuitet" | "rak") {
  const r = annualRate / 100 / 12;
  const rows: number[] = [];
  if (type === "annuitet") {
    const pay = annuityPayment(amount, months, r);
    for (let i = 0; i < months; i++) rows.push(pay);
  } else {
    const amort = amount / months;
    let debt = amount;
    for (let i = 0; i < months; i++) {
      rows.push(amort + debt * r);
      debt -= amort;
    }
  }
  return rows;
}

/** Effective annual rate via bisection on monthly IRR */
function effectiveRate(net: number, payments: number[]) {
  if (net <= 0) return 0;
  const npv = (r: number) => payments.reduce((s, p, i) => s + p / Math.pow(1 + r, i + 1), 0) - net;
  let lo = 0, hi = 1;
  if (npv(0) <= 0) return 0;
  for (let i = 0; i < 200; i++) {
    const mid = (lo + hi) / 2;
    if (npv(mid) > 0) lo = mid;
    else hi = mid;
  }
  const r = (lo + hi) / 2;
  return (Math.pow(1 + r, 12) - 1) * 100;
}

/** Only lenders we have an affiliate/tracking link for */
const AFFILIATE_LENDERS: Lender[] = LENDER_LIST.filter((l) => {
  const url = APPLY_URLS[l.id] ?? "";
  return url.includes("/t/t?a=");
});

/* ---------- tabs ---------- */

type TabId = "lanekalkyl" | "svart" | "privatlan" | "billan";

const TABS: { id: TabId; label: string }[] = [
  { id: "lanekalkyl", label: "Lånekalkyl" },
  { id: "svart", label: "Svårt att få lån" },
  { id: "privatlan", label: "Privatlån" },
  { id: "billan", label: "Billån" },
];

const CALC_DEFAULTS: Record<string, { amount: number; years: number; rate: number; heading: string; sub: string }> = {
  lanekalkyl: { amount: 100000, years: 12, rate: 0, heading: "Lånekalkyl för privatlån", sub: "Räkna ut månadskostnad och ränta på lån" },
  privatlan: { amount: 150000, years: 8, rate: 7.5, heading: "Lånekalkyl för privatlån", sub: "Räkna ut vad ditt privatlån kostar per månad" },
  billan: { amount: 200000, years: 6, rate: 5.2, heading: "Lånekalkyl för billån", sub: "Räkna ut månadskostnaden för ditt billån" },
};

function Kalkyl() {
  const [tab, setTab] = useState<TabId>("lanekalkyl");

  return (
    <EpiProvider value="guider-lanekalkyl">
    <InfoLayout title={tab === "svart" ? "Om du har svårt att få lån" : "Lånekalkyl"} showFaq={false}>
      <div className="not-prose flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`rounded-md border px-4 py-2 text-sm font-semibold transition ${
              t.id === tab
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-card text-foreground hover:border-primary/50"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "svart" ? <SvartAttFaLan /> : <CalculatorTab key={tab} tab={tab} />}
    </InfoLayout>
    </EpiProvider>
  );
}

/* ---------- calculator ---------- */

function CalculatorTab({ tab }: { tab: TabId }) {
  const d = CALC_DEFAULTS[tab] ?? CALC_DEFAULTS.lanekalkyl;
  const sp = Route.useSearch();
  const clampAmount = (n: number) => Math.min(800000, Math.max(1000, Math.round(n / 1000) * 1000));
  const clampMonths = (n: number) => Math.min(144, Math.max(3, Math.round(n)));
  const [amount, setAmount] = useState(sp.amount ? clampAmount(sp.amount) : d.amount);
  const [months, setMonths] = useState(
    sp.months ? clampMonths(sp.months) : sp.years ? clampMonths(sp.years * 12) : d.years * 12,
  );
  const [rate, setRate] = useState(sp.rate ?? d.rate);
  const [setupFee, setSetupFee] = useState(sp.setup ?? 0);
  const [aviFee, setAviFee] = useState(sp.avi ?? 0);
  const [type, setType] = useState<"annuitet" | "rak">(sp.type ?? "annuitet");

  

  const { first, last, total, eff } = useMemo(() => {
    const rows = schedule(amount, months, rate, type);
    const withFees = rows.map((p) => p + aviFee);
    const totalSum = withFees.reduce((a, b) => a + b, 0);
    return {
      first: withFees[0] ?? 0,
      last: withFees[withFees.length - 1] ?? 0,
      total: totalSum + setupFee,
      eff: effectiveRate(amount - setupFee, withFees),
    };
  }, [amount, months, rate, type, aviFee, setupFee]);

  const lenders = useMemo(() => {
    const base = tab === "billan"
      ? AFFILIATE_LENDERS.filter((l) => l.categories.includes("Billån"))
      : AFFILIATE_LENDERS;
    return base.filter((l) => amount >= l.minAmount && amount <= l.maxAmount).slice(0, 12);
  }, [tab, amount]);

  return (
    <div className="space-y-8">
      <p className="text-lg font-semibold text-foreground">{d.sub}!</p>
      <h2>{d.heading}</h2>

      <LoanWarning />

      <div className="not-prose grid gap-6 rounded-md border border-border bg-card p-5 sm:p-6 md:grid-cols-2">
        <div>
          <div className="font-display text-lg font-bold text-foreground">Lånebelopp: {formatKr(amount)}</div>
          <input
            type="range" min={1000} max={800000} step={1000} value={amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            className="range-slider mt-3 w-full"
            style={{ ["--val" as any]: `${((amount - 1000) / 799000) * 100}%` }}
          />
          <div className="mt-1 flex justify-between text-xs text-muted-foreground"><span>1 000 kr</span><span>800 000 kr</span></div>
        </div>

        <div>
          <div className="font-display text-lg font-bold text-foreground">
            Återbetalningstid: {months} {months === 1 ? "månad" : "månader"}
            {months >= 12 ? ` (${(months / 12).toFixed(months % 12 === 0 ? 0 : 1).replace(".", ",")} år)` : ""}
          </div>
          <input
            type="range" min={3} max={144} step={1} value={months}
            onChange={(e) => setMonths(Number(e.target.value))}
            className="range-slider mt-3 w-full"
            style={{ ["--val" as any]: `${((months - 3) / 141) * 100}%` }}
          />
          <div className="mt-1 flex justify-between text-xs text-muted-foreground"><span>3 månader</span><span>144 månader (12 år)</span></div>
        </div>

        <NumberField label="Nominell ränta" suffix="%" value={rate} step={0.01} onChange={setRate} />
        <NumberField label="Avi-avgift" suffix="Kr" value={aviFee} step={1} onChange={setAviFee} />
        <NumberField label="Uppläggningsavgift" suffix="Kr" value={setupFee} step={1} onChange={setSetupFee} />

        <div>
          <div className="mb-2 font-display text-lg font-bold text-foreground">Amorteringstyp</div>
          <div className="grid grid-cols-2 gap-2 rounded-md border border-input p-1">
            {(["annuitet", "rak"] as const).map((v) => (
              <button
                key={v}
                onClick={() => setType(v)}
                className={`rounded-md px-3 py-2.5 text-sm font-semibold transition ${
                  type === v ? "bg-primary-soft text-primary ring-1 ring-primary" : "text-foreground hover:bg-muted"
                }`}
              >
                {v === "annuitet" ? "Annuitet" : "Rak amortering"}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="not-prose grid gap-4 rounded-md bg-primary-soft p-5 sm:grid-cols-4">
        <Result label={type === "rak" ? "Månadskostnad (första)" : "Månadskostnad"} value={formatKr(first)} primary />
        {type === "rak" && <Result label="Månadskostnad (sista)" value={formatKr(last)} />}
        <Result label="Räntekostnad" value={formatKr(Math.max(0, total - amount))} />
        <Result label="Total kostnad" value={formatKr(total)} />
        {type !== "rak" && <Result label="Effektiv ränta" value={formatPct(eff)} />}
      </div>

      <LenderTable lenders={lenders} epi2="comparison-table-before" />

      {tab === "lanekalkyl" ? <CalculatorContent /> : null}
    </div>
  );
}

function NumberField({ label, suffix, value, step, onChange }: { label: string; suffix: string; value: number; step: number; onChange: (n: number) => void }) {
  return (
    <div>
      <div className="mb-2 font-display text-lg font-bold text-foreground">{label}</div>
      <div className="flex items-center rounded-md border border-input bg-card px-4">
        <input
          type="number" min={0} step={step} value={value}
          onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
          className="w-full bg-transparent py-3 text-[15px] outline-none"
        />
        <span className="pl-2 font-semibold text-foreground">{suffix}</span>
      </div>
    </div>
  );
}

function Result({ label, value, primary = false }: { label: string; value: string; primary?: boolean }) {
  return (
    <div className="text-center">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className={`mt-1 font-display text-lg font-black ${primary ? "text-primary" : "text-foreground"}`}>{value}</div>
    </div>
  );
}

/* ---------- editorial: calculator ---------- */

function CalculatorContent() {
  return (
    <div className="space-y-4">
      <h2>Om lånekalkylatorn</h2>
      <p>
        Med vår lånekalkyl räknar du enkelt ut månadskostnad, räntekostnad och effektiv ränta på ett lån. Du ser direkt hur
        lånebelopp, återbetalningstid, avgifter och amorteringstyp påverkar vad lånet faktiskt kostar dig.
      </p>
      <p>
        Kalkylen passar dig som vill räkna på ett privatlån, även kallat blancolån (lån utan säkerhet), på mellan
        10 000 och 800 000 kronor.
      </p>
      <ul className="list-disc space-y-1 pl-5">
        <li>Vad blir månadskostnaden för lånet?</li>
        <li>Hur mycket har jag råd att låna?</li>
        <li>Hur påverkar belopp, ränta och löptid den totala kostnaden?</li>
      </ul>

      <h2>Räkna ut månadskostnaden på ett lån</h2>
      <p>
        Månadskostnaden beror på lånebeloppet, återbetalningstiden, den nominella räntan, avgifter som avi- och
        uppläggningsavgift samt vilken amorteringstyp du väljer. Fyll i fälten ovan:
      </p>
      <ul className="list-disc space-y-1 pl-5">
        <li><b>Lånebelopp</b> – välj ett belopp mellan 10 000 och 800 000 kronor.</li>
        <li><b>Återbetalningstid</b> – hur länge du betalar av lånet, vanligtvis 2–12 år.</li>
        <li><b>Nominell ränta</b> – den årliga räntekostnaden i procent.</li>
        <li><b>Avi-avgift</b> – administrativ avgift som tas ut varje månad.</li>
        <li><b>Uppläggningsavgift</b> – engångsavgift när lånet startas.</li>
        <li><b>Amorteringstyp</b> – annuitet eller rak amortering.</li>
      </ul>

      <h2>Amorteringstyper</h2>
      <h3>Annuitet</h3>
      <p>
        Ett annuitetslån innebär att du betalar samma totala belopp varje månad under hela löptiden, så länge räntan är
        oförändrad. Betalningen består av både ränta och amortering – i början är räntedelen större, men amorteringsdelen
        ökar efter hand.
      </p>
      <h3>Rak amortering</h3>
      <p>
        Med rak amortering betalar du av lika mycket på skulden varje månad. Räntekostnaden minskar i takt med att skulden
        blir mindre, vilket gör att månadsbetalningen sjunker successivt. Rak amortering är normalt något billigare totalt
        sett än ett annuitetslån.
      </p>

      <h2>Bra att tänka på innan du lånar</h2>
      <ul className="list-disc space-y-1 pl-5">
        <li>Kolla alltid upp vad den totala kostnaden blir innan du ansöker.</li>
        <li>Fundera på hur mycket du faktiskt behöver låna – gör en realistisk budget.</li>
        <li>Titta inte bara på månadskostnaden utan även den totala kostnaden över hela löptiden.</li>
        <li>Försök betala tillbaka lånet så snabbt din ekonomi tillåter.</li>
        <li>Avi- och uppläggningsavgifter påverkar priset – jämför alltid den effektiva räntan.</li>
      </ul>

      <h2>Effektiv ränta</h2>
      <p>
        Den effektiva räntan består av den nominella räntan plus samtliga avgifter och speglar därför den verkliga
        årskostnaden för lånet. Det är den effektiva räntan du ska jämföra när du väljer långivare.
      </p>

      <h2>När du ansöker om ett lån</h2>
      <p>
        Långivaren gör en kreditbedömning där en kreditupplysning tas för att bedöma din kreditvärdighet. Beviljas lånet
        erbjuds du en individuellt satt ränta baserad på din ekonomi. Ansöker du via en låneförmedlare räcker det oftast med
        en enda kreditupplysning även om flera banker lämnar bud.
      </p>

      <h2>Om Svensk Låneguide</h2>
      <p>
        Svensk Låneguide är en oberoende jämförelsetjänst för lån och krediter i Sverige. Tjänsten är kostnadsfri för dig –
        vi får ersättning via annonslänkar från långivare, vilket aldrig påverkar vilka villkor du erbjuds.
      </p>
    </div>
  );
}

/* ---------- editorial: svårt att få lån ---------- */

const SVART_FAQ = [
  { q: "Varför är det svårt att få lån?", a: "De flesta banker använder en standardiserad kreditmodell. Faller du utanför ramen – till exempel på grund av många kreditupplysningar, låg eller oregelbunden inkomst eller en gammal betalningsanmärkning – blir det ett automatiskt nej, även om du egentligen skulle klara av att betala lånet." },
  { q: "Hur får man lån när alla säger nej?", a: "Vänd dig till långivare som tittar mer på din nuvarande betalningsförmåga än på din historik. Att ansöka via en förmedlare ger flera bud på en enda kreditupplysning, och en medsökande höjer chansen att bli beviljad rejält." },
  { q: "Kan man få lån med många kreditupplysningar?", a: "Ja. Flera långivare på sajten använder andra kreditupplysningsbolag än UC och lägger mindre vikt vid antalet tidigare förfrågningar. Har du möjlighet är det ändå klokt att vänta tills några upplysningar hunnit bli äldre." },
  { q: "Kan man få lån med betalningsanmärkningar?", a: "Ja, det finns långivare som beviljar lån trots en eller flera betalningsanmärkningar. De ser till din inkomst och dina nuvarande utgifter. Räntan blir dock högre, så jämför alltid den effektiva räntan." },
  { q: "Kan man få lån utan fast anställning?", a: "Ja. Vikarier, timanställda, egenföretagare och frilansare kan beviljas lån, men långivaren vill oftast se en stabil inkomsthistorik över några månader eller ett par deklarerade år." },
  { q: "Hur mycket lån kan jag få?", a: "Det beror på din inkomst, dina befintliga skulder och långivarens kvar-att-leva-på-kalkyl. Ansök om det belopp du faktiskt behöver – det ökar chansen att bli beviljad och sänker kostnaden." },
];

function SvartAttFaLan() {
  const lenders = AFFILIATE_LENDERS.filter((l) => l.allowsRemarks).slice(0, 12);
  return (
    <div className="space-y-4">
      <p className="text-lg font-semibold text-foreground">
        Har du blivit nekad av flera långivare? Här jämför du aktörer med anpassade villkor
      </p>


      <h2>Det behöver inte vara omöjligt att få ett lån</h2>
      <p>
        Om du blivit nekad av flera olika banker kan det kännas hopplöst. Många långivare följer en helt standardiserad
        process för vilka kunder de beviljar lån. Faller du bara lite utanför ramen blir det ett nej – även om du faktiskt
        skulle kunna betala både ränta och amortering utan problem. Det finns långivare som istället fokuserar på din
        nuvarande betalningsförmåga.
      </p>

      <h2>Lån när alla säger nej</h2>
      <p>
        Många låntagare har en fullgod återbetalningsförmåga trots en gammal betalningsanmärkning, avsaknad av fast
        anställning eller många kreditupplysningar. Därför erbjuder en del långivare anpassade villkor till dig som har
        svårt att få lån hos de traditionella storbankerna.
      </p>

      <LenderTable lenders={lenders} epi2="comparison-table-after" />

      <h2>Situationer som kan göra det svårt att få ett lån</h2>
      <h3>Många kreditupplysningar</h3>
      <p>
        Har du ansökt hos många långivare på kort tid har det troligen tagits flera kreditupplysningar på dig. Det kan sänka
        din kreditvärdighet och göra att bankerna säger nej. Vissa långivare ser istället till din nuvarande
        återbetalningsförmåga, och ansöker du via en förmedlare räcker det med en enda upplysning för flera bud.
      </p>
      <h3>Betalningsanmärkningar</h3>
      <p>
        En anmärkning kan uppstå av många skäl – sjukdom, en missad räkning eller en tvist som gått till inkasso. De flesta
        storbanker säger nej direkt, men flera nischbanker gör en individuell bedömning där historiken väger lättare än din
        ekonomi i dag.
      </p>
      <h3>Utan fast inkomst eller anställning</h3>
      <p>Anställningsformer som ofta gör det svårare att få lån:</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>Visstids- och projektanställning</li>
        <li>Vikariat och timanställning</li>
        <li>Säsongs- och behovsanställning</li>
        <li>Frilansare och giganställda</li>
        <li>Egenföretagare med enskild firma eller konsult med eget bolag</li>
        <li>Nyinflyttad i Sverige med nytt arbete</li>
      </ul>

      <h2>Förbättra din kreditvärdighet</h2>
      <ul className="list-disc space-y-1 pl-5">
        <li>Kontrollera din kreditvärdighet, till exempel hos UC.</li>
        <li>Minska antalet kreditupplysningar – vänta om du kan tills några försvunnit ur registren.</li>
        <li>Minska dina skulder och säg upp outnyttjade kreditkort och krediter.</li>
        <li>Samla dyra lån och krediter hos en långivare för lägre ränta och månadskostnad.</li>
        <li>Betala alla fakturor i tid – en anmärkning finns kvar i tre år.</li>
        <li>Visa lönebesked och andra underlag som styrker din betalningsförmåga.</li>
        <li>Jämför flera långivare istället för att ansöka blint hos en i taget.</li>
      </ul>

      <h2>Risker med lån</h2>
      <p>
        Alla lån innebär en risk för din ekonomi. Pengarna ska betalas tillbaka med ränta, och skulle något inträffa –
        arbetslöshet, sjukdom, olycksfall eller separation – måste lånet ändå betalas. Räntan kan dessutom stiga och göra
        lånet dyrare än du räknat med. Fundera alltid igenom riskerna och om du verkligen har råd innan du lånar.
      </p>

      <FaqSection items={SVART_FAQ} className="!px-0" />
    </div>
  );
}
